import React, { useState, useContext, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { UserContext } from "../context/userContext"
import { FirebaseContext } from "../firebase"
import { gql, useQuery } from "@apollo/client"

// Components
import Notifications from "../components/notifications/Notifications"
import Appointments from "../components/Appointments/Appointments"
import DailyPlanner from "../components/DailyPlanner/DailyPlanner"
import CallStatus from "../components/CallStatus"
import StaffProfileMenu from "../nav/NavHeader/StaffProfileMenu"
import DeveloperTools from "../components/DeveloperTools/DeveloperTools"
import SupportLink from "../nav/NavHeader/SupportLink"
import OrganizationLogo from "../components/OrganizationLogo"
import SelectorContainer from "../nav/NavHeader/Selectors/SelectorContainer"
import { PARTNER_ADMIN_ROLE } from "../helpers/staffRoleHelpers"
import styles from "../styles/Header.module.css"

// GraphQL query
const STAFF_MEMBERSHIP_ORGANIZATION = gql`
  query getStaffMembershipDetailByStaffID(
    $staff_id: ID!
    $organization_id: ID
    $staff_membership_id: ID!
  ) {
    getStaffMembershipDetailByStaffID(
      staff_id: $staff_id
      organization_id: $organization_id
    ) {
      membership_list
    }
    userAdmin: staffDataByStaffMembershipID(
      staff_membership_id: $staff_membership_id
    ) {
      organization_id
    }
  }
`

export default function Header({
  isSidebarCollapsed = false,
  onToggleSidebar = () => {},
  canToggleSidebar = true,
  isSidebarToggleClickDisabled = false,
  onSidebarToggleHoverStart = () => {},
  onSidebarToggleHoverEnd = () => {},
}) {
  const user = useContext(UserContext)
  const firebase = useContext(FirebaseContext)
  const [organizationName, setOrganizationName] = useState("")

  // States
  const [toggles, setToggles] = useState({
    profile: false,
    organizationMembership: false,
  })

  const [currentStaffMembership, setCurrentStaffMembership] = useState("")
  const [staffAdminMembershipOrg, setStaffAdminMembershipOrg] = useState("")

  const profileMenuRef = useRef()

  // GraphQL query to fetch staff membership and organization details
  const { data, refetch } = useQuery(STAFF_MEMBERSHIP_ORGANIZATION, {
    variables: {
      staff_id: user.session.user.staff_id,
      organization_id: user.session.user.organization_id,
      staff_membership_id: user.session.user.staff_membership_id,
    },
  })

  // Refetch data when the organization_id changes
  useEffect(() => {
    refetch()
  }, [user.session.user.organization_id, refetch])

  // Update state based on fetched data
  useEffect(() => {
    setStaffAdminMembershipOrg(data?.userAdmin?.organization_id)

    if (data?.getStaffMembershipDetailByStaffID?.membership_list[0]) {
      setCurrentStaffMembership(
        data.getStaffMembershipDetailByStaffID.membership_list[0],
      )
      setToggles((prev) => ({ ...prev, organizationMembership: true }))
    } else {
      setToggles((prev) => ({ ...prev, organizationMembership: false }))
      setCurrentStaffMembership("")
    }
  }, [data, user.session.user.organization_id])

  // Close profile menu when clicking outside of it
  useEffect(() => {
    const profileHandler = (e) => {
      if (!profileMenuRef.current.contains(e.target)) {
        setToggles((prev) => ({ ...prev, profile: false }))
      }
    }

    document.addEventListener("mousedown", profileHandler)

    return () => {
      document.removeEventListener("mousedown", profileHandler)
    }
  }, [])

  // Listen for changes in the organizations collection and update the organization name accordingly
  useEffect(() => {
    const organizationRef = firebase.db
      .collection("enterprise-config")
      .doc(user.session.user.enterprise_id)
      .collection("organizations")
      .doc(user.session.user.organization_id)

    const unsubscribe = organizationRef.onSnapshot(
      (snapshot) => {
        if (snapshot.exists) {
          const data = snapshot.data()
          setOrganizationName(data.organization_name || "") // Ensure a default value
        }
      },
      (error) => {
        console.log("Error listening to organizations collection:", error)
      },
    )

    return () => unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.session.user.enterprise_id, user.session.user.organization_id])

  const handleSidebarToggleClick = () => {
    if (isSidebarToggleClickDisabled) {
      return
    }

    onToggleSidebar()
  }

  return (
    <>
      <header className={styles.headerRoot}>
        <div className="wrapper">
          <div className={styles.brandCluster}>
            {canToggleSidebar ? (
              <button
                type="button"
                className={styles.sidebarToggleButton}
                onClick={handleSidebarToggleClick}
                onMouseEnter={onSidebarToggleHoverStart}
                onMouseLeave={onSidebarToggleHoverEnd}
                onFocus={onSidebarToggleHoverStart}
                onBlur={onSidebarToggleHoverEnd}
                aria-disabled={isSidebarToggleClickDisabled}
                aria-label={
                  isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
                }
                title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isSidebarCollapsed ? (
                  <span className="material-symbols-outlined" aria-hidden="true">
                    right_panel_close
                  </span>
                ) : (
                  <span className="material-symbols-outlined" aria-hidden="true">
                    right_panel_open
                  </span>
                )}
              </button>
            ) : null}

            <Link to="/" className={styles.orgLogoHomeLink} aria-label="Go to dashboard">
              <OrganizationLogo
                className={styles.headerOrgLogo}
                renderWhenNoLogo={true}
                noLogoVariant="initials"
                alt="Organization logo"
              />
            </Link>

            <div className={styles.headerSelectorWrap}>
              <SelectorContainer />
            </div>
          </div>

          <ul className={`nav-top ${styles.navTop}`}>
            <DeveloperTools />
            <CallStatus />
            <Notifications />

            {user.session.user.staff_role < PARTNER_ADMIN_ROLE && (
              <>
                <DailyPlanner />
                <Appointments />
              </>
            )}

            {/* Support link */}
            <SupportLink />
          </ul>

          {/* Profile Menu - MOVE TO COMPONENT */}
          <StaffProfileMenu
            profileMenuRef={profileMenuRef}
            toggles={toggles}
            setToggles={setToggles}
            currentStaffMembership={currentStaffMembership}
            staffAdminMembershipOrg={staffAdminMembershipOrg}
            organizationName={organizationName}
            firebase={firebase}
          />
        </div>
      </header>
    </>
  )
}
